import { memo, useCallback, useState, type FC } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { FilterMethod } from "@/generated/company-search";
import {
  useCompanyFilterActions,
  useCompanyFilterOptions,
  useCompanyFilterSlice,
} from "../../hooks";
import { CsvUploadDropzone } from "../../components/CsvUploadDropzone";
import {
  UploadingState,
  SuccessState,
  ErrorState,
  MatchingOptions,
} from "../uploadedAccounts/components";
import { defaultKnownCompaniesSlice, type KnownCompaniesSlice } from "./slice";
import type { KnownCompaniesOptions } from "./options";
import { detectHgIdColumn } from "./detectHgIdColumn";
import { useUploadPolling } from "../uploadedAccounts/hooks";
import type { PollResponse } from "../uploadedAccounts/options";

const TEST_ID_PREFIX = "known-companies";
// Matches the 2GB limit of /company-search-api/graphql/upload (nginx and cs-api)
const MAX_FILE_SIZE_BYTES = 2 * 1000 * 1000 * 1000;

/**
 * SuperKudu-only upload of a list of HG IDs. Unlike Uploaded Accounts there is
 * no fuzzy matching: the IDs are stored as-is in S3 by hermes and applied
 * through its `known_companies` filter.
 */
export const KnownCompaniesFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("knownCompanies") as KnownCompaniesSlice;
  const { setSlice } = useCompanyFilterActions();
  const options = useCompanyFilterOptions("knownCompanies") as
    | KnownCompaniesOptions
    | undefined;
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);

  useUploadPolling({
    fileId: slice.identifier,
    isProcessing: slice.uploadState === "PROCESSING",
    onPoll: options?.onPoll,
    onSuccess: useCallback(
      (stats: PollResponse) => {
        setSlice("knownCompanies", (prev) => ({
          ...prev,
          uploadState: "SUCCESS",
          totalRows: stats.totalRows,
          invalidRows: stats.invalidRows,
          matchedRows: stats.matchedRows,
          uploadedAt: stats.lastMatched,
        }));
        setShowSuccessBanner(true);
        setTimeout(() => setShowSuccessBanner(false), 3000);
      },
      [setSlice]
    ),
    onError: useCallback(
      (error: Error) => {
        setSlice("knownCompanies", (prev) => ({
          ...prev,
          uploadState: "ERROR",
          error: error.message,
        }));
      },
      [setSlice]
    ),
  });

  const handleFileAccepted = useCallback(
    async (files: File[]) => {
      const [file] = files;
      if (!file || !options?.onUpload) return;

      setSlice("knownCompanies", (prev) => ({
        ...defaultKnownCompaniesSlice,
        uploadState: "UPLOADING",
        fileName: file.name,
        filterMethod: prev.filterMethod,
      }));

      try {
        const column = await detectHgIdColumn(file);
        const { identifier } = await options.onUpload(file, column);
        setSlice("knownCompanies", (prev) => ({
          ...prev,
          uploadState: "PROCESSING",
          identifier,
        }));
      } catch (error) {
        setSlice("knownCompanies", (prev) => ({
          ...prev,
          uploadState: "ERROR",
          error: error instanceof Error ? error.message : "Upload failed",
        }));
      }
    },
    [options, setSlice]
  );

  const handleFilterMethodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setSlice("knownCompanies", (prev) => ({
        ...prev,
        filterMethod: checked
          ? FilterMethod.EXCLUDE_MATCHED
          : FilterMethod.FILTER,
      }));
    },
    [setSlice]
  );

  const handleReset = useCallback(() => {
    setSlice("knownCompanies", (prev) => ({
      ...defaultKnownCompaniesSlice,
      filterMethod: prev.filterMethod,
    }));
  }, [setSlice]);

  const { uploadState } = slice;
  const isLoading = uploadState === "UPLOADING" || uploadState === "PROCESSING";

  return (
    <Box sx={{ mx: -2, mt: -2 }} data-testid="known-companies-filter">
      {uploadState === "IDLE" && (
        <CsvUploadDropzone
          id="known-companies-file"
          maxSizeBytes={MAX_FILE_SIZE_BYTES}
          helperText="Max size: 2GB"
          onFilesAccepted={handleFileAccepted}
        >
          <Box
            sx={{
              width: "100%",
              borderTop: 1,
              borderColor: "divider",
              pt: 1.5,
              mt: 0.5,
              textAlign: "center",
            }}
          >
            <Chip
              label="HG ID"
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ fontSize: 10, mt: 1 }}
            >
              Only the HG ID column is used. A single-column file without a
              header also works.
            </Typography>
          </Box>
        </CsvUploadDropzone>
      )}

      {isLoading && (
        <UploadingState
          uploadState={uploadState as "UPLOADING" | "PROCESSING"}
          fileName={slice.fileName}
          totalRows={slice.totalRows}
        />
      )}

      {uploadState === "SUCCESS" && (
        <SuccessState
          fileName={slice.fileName}
          totalRows={slice.totalRows}
          matchedRows={slice.matchedRows}
          invalidRows={slice.invalidRows}
          uploadedAt={slice.uploadedAt}
          showSuccessBanner={showSuccessBanner}
          fileId={slice.identifier}
          onClear={handleReset}
          testIdPrefix={TEST_ID_PREFIX}
        />
      )}

      {uploadState === "ERROR" && (
        <ErrorState error={slice.error} onRetry={handleReset} />
      )}

      {!isLoading && (
        <MatchingOptions
          filterMethod={slice.filterMethod}
          onFilterMethodChange={handleFilterMethodChange}
          testIdPrefix={TEST_ID_PREFIX}
        />
      )}
    </Box>
  );
});

KnownCompaniesFilter.displayName = "KnownCompaniesFilter";
